// src/products/dataroom/pages/UsersPage.js
import React, { useEffect, useState } from 'react';
import { Users, Plus, RefreshCw, Mail, User, AlertCircle, Building2, UserCheck, Send, Trash2, Ban, Edit2, ChevronDown, ChevronRight } from 'lucide-react';
import useDataroomStore from '../state/dataroomStore';
import { dataroomInviteIntegration } from '../services/dataroomInviteIntegration';
import AddUserModal from '../components/Modals/AddUserModal';
import RenameModal from '../components/Modals/RenameModal';
import { supabase } from '../../../config';
import { formatDate } from '../utils/dateUtils';

const UsersPage = ({ userRole, onMessage }) => {
  const {
    users,
    loading,
    errors,
    loadWizardUsers,
    selectedProjectId,
    projects
  } = useDataroomStore();

  const isLoadingUsers = loading?.users || false;
  const usersError = errors?.users || null;

  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [sendingInvites, setSendingInvites] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // {user, action: 'revoke' | 'delete'}
  const [renameUser, setRenameUser] = useState(null);
  const [expandedProjects, setExpandedProjects] = useState({});
  const [projectNames, setProjectNames] = useState({});

  useEffect(() => {
    console.log('UsersPage: Loading wizard users...');
    console.log('UsersPage: loading state:', loading);
    console.log('UsersPage: errors state:', errors);
    
    // Load wizard users for all projects
    loadWizardUsers(null); // null means load all
    
    // Load project names for display
    loadProjectNames();
  }, [loadWizardUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjectNames = async () => {
    try {
      const { data, error } = await supabase
        .from('dataroom_projects')
        .select('id, project_name');
      
      if (error) throw error;
      
      const nameMap = {};
      (data || []).forEach(project => {
        nameMap[project.id] = project.project_name;
      });
      setProjectNames(nameMap);
    } catch (err) {
      console.error('Error loading project names:', err);
    }
  };

  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    const newSelection = new Set(selectedUsers);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUsers(newSelection);
  };

  // Send invites to selected users
  const sendInvitesToSelected = async () => {
    if (selectedUsers.size === 0) {
      onMessage && onMessage('Please select at least one user to invite', 'warning');
      return;
    }

    setSendingInvites(true);
    const results = [];

    try {
      const usersToInvite = users.filter(u => selectedUsers.has(u.id));
      
      for (const user of usersToInvite) {
        // Check if user is already invited/active
        if (user.status === 'active') {
          results.push({ email: user.email, status: 'skipped', message: 'User is already active' });
          continue;
        }

        // Get project ID from user's first party (users can belong to multiple projects)
        const userProjectId = user.parties?.[0]?.projectId || selectedProjectId;
        
        if (!userProjectId) {
          results.push({ 
            email: user.email, 
            status: 'error', 
            message: 'No project ID found for user' 
          });
          continue;
        }

        // Send invite using UserInvites API with dataroom integration
        const result = await dataroomInviteIntegration.sendDataroomInviteWithGroups({
          email: user.email,
          projectId: userProjectId,
          role: user.role || 'viewer',
          groupIds: [], // Will use default group
          customMessage: `You have been invited to view the DealRoom.`,
          invitedBy: null // Will be set by RLS
        });

        results.push({
          email: user.email,
          status: result.status,
          message: result.message
        });
      }

      const successful = results.filter(r => r.status === 'success').length;
      const failed = results.filter(r => r.status !== 'success' && r.status !== 'skipped').length;
      const skipped = results.filter(r => r.status === 'skipped').length;

      onMessage && onMessage(
        `Invites sent: ${successful} successful, ${failed} failed, ${skipped} skipped`,
        failed > 0 ? 'warning' : 'success'
      );

      // Refresh user list to update statuses
      loadWizardUsers(null);
      setSelectedUsers(new Set());

    } catch (error) {
      console.error('Error sending invites:', error);
      onMessage && onMessage('Failed to send invites: ' + error.message, 'error');
    } finally {
      setSendingInvites(false);
    }
  };

  // Send invite to single user
  const sendSingleInvite = async (user) => {
    // Get project ID from user's first party
    const userProjectId = user.parties?.[0]?.projectId || selectedProjectId;
    
    if (!userProjectId) {
      onMessage && onMessage('No project found for this user', 'error');
      return;
    }

    setSendingInvites(true);

    try {
      const result = await dataroomInviteIntegration.sendDataroomInviteWithGroups({
        email: user.email,
        projectId: userProjectId,
        role: user.role || 'viewer',
        groupIds: [],
        customMessage: `You have been invited to view the dataroom.`,
        invitedBy: null
      });

      if (result.status === 'success') {
        onMessage && onMessage(`Invite sent to ${user.email}`, 'success');
        loadWizardUsers(null);
      } else {
        onMessage && onMessage(`Failed to send invite: ${result.message}`, 'error');
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      onMessage && onMessage('Failed to send invite: ' + error.message, 'error');
    } finally {
      setSendingInvites(false);
    }
  };

  // Block user (keeps user but disables Viewer access)
  const handleBlockUser = async (user) => {
    const userProjectId = user.parties?.[0]?.projectId || user.projectId || selectedProjectId;
    
    if (!userProjectId) {
      onMessage && onMessage('No project found for this user', 'error');
      return;
    }

    try {
      // First, find the user record to get the correct identifier
      // Try email first, then user_id if email doesn't match
      let userRecord = null;
      let findError = null;
      
      // Try to find by email first
      const { data: emailRecord, error: emailError } = await supabase
        .from('dataroom_users')
        .select('user_id, email')
        .eq('project_id', userProjectId)
        .eq('email', user.email)
        .maybeSingle();
      
      if (emailRecord) {
        userRecord = emailRecord;
      } else {
        // If not found by email, try by user_id (in case email is stored as user_id)
        const { data: idRecord, error: idError } = await supabase
          .from('dataroom_users')
          .select('user_id, email')
          .eq('project_id', userProjectId)
          .eq('user_id', user.email)
          .maybeSingle();
        
        if (idError && !emailError) findError = idError;
        else if (idError) findError = emailError;
        else userRecord = idRecord;
      }

      if (findError) throw findError;

      if (!userRecord) {
        throw new Error('User not found in this Dealroom');
      }

      // Build update query using the identifier from the database
      const identifier = userRecord.user_id || userRecord.email;
      const identifierField = userRecord.user_id ? 'user_id' : 'email';

      const { error } = await supabase
        .from('dataroom_users')
        .update({
          status: 'blocked',
          last_active: new Date().toISOString()
        })
        .eq(identifierField, identifier)
        .eq('project_id', userProjectId);

      if (error) throw error;

      onMessage && onMessage(`User ${user.email} has been blocked from this Dealroom`, 'success');
      loadWizardUsers(null);
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error blocking user:', error);
      onMessage && onMessage('Failed to block user: ' + error.message, 'error');
    }
  };

  // Unblock user (restores access)
  const handleUnblockUser = async (user) => {
    const userProjectId = user.parties?.[0]?.projectId || user.projectId || selectedProjectId;
    
    if (!userProjectId) {
      onMessage && onMessage('No project found for this user', 'error');
      return;
    }

    try {
      // First, find the user record to get the correct identifier
      // Try email first, then user_id if email doesn't match
      let userRecord = null;
      let findError = null;
      
      // Try to find by email first
      const { data: emailRecord, error: emailError } = await supabase
        .from('dataroom_users')
        .select('user_id, email')
        .eq('project_id', userProjectId)
        .eq('email', user.email)
        .maybeSingle();
      
      if (emailRecord) {
        userRecord = emailRecord;
      } else {
        // If not found by email, try by user_id (in case email is stored as user_id)
        const { data: idRecord, error: idError } = await supabase
          .from('dataroom_users')
          .select('user_id, email')
          .eq('project_id', userProjectId)
          .eq('user_id', user.email)
          .maybeSingle();
        
        if (idError && !emailError) findError = idError;
        else if (idError) findError = emailError;
        else userRecord = idRecord;
      }

      if (findError) throw findError;

      if (!userRecord) {
        throw new Error('User not found in this Dealroom');
      }

      // Build update query using the identifier from the database
      const identifier = userRecord.user_id || userRecord.email;
      const identifierField = userRecord.user_id ? 'user_id' : 'email';

      const { error } = await supabase
        .from('dataroom_users')
        .update({
          status: 'active',
          last_active: new Date().toISOString()
        })
        .eq(identifierField, identifier)
        .eq('project_id', userProjectId);

      if (error) throw error;

      onMessage && onMessage(`User ${user.email} has been unblocked and can access this Dealroom`, 'success');
      loadWizardUsers(null);
    } catch (error) {
      console.error('Error unblocking user:', error);
      onMessage && onMessage('Failed to unblock user: ' + error.message, 'error');
    }
  };

  // Revoke user access (soft delete - keeps record)
  const handleRevokeAccess = async (user) => {
    const userProjectId = user.parties?.[0]?.projectId || user.projectId || selectedProjectId;
    
    if (!userProjectId) {
      onMessage && onMessage('No project found for this user', 'error');
      return;
    }

    try {
      // First, find the user record to get the correct identifier
      // Try email first, then user_id if email doesn't match
      let userRecord = null;
      let findError = null;
      
      // Try to find by email first
      const { data: emailRecord, error: emailError } = await supabase
        .from('dataroom_users')
        .select('user_id, email')
        .eq('project_id', userProjectId)
        .eq('email', user.email)
        .maybeSingle();
      
      if (emailRecord) {
        userRecord = emailRecord;
      } else {
        // If not found by email, try by user_id (in case email is stored as user_id)
        const { data: idRecord, error: idError } = await supabase
          .from('dataroom_users')
          .select('user_id, email')
          .eq('project_id', userProjectId)
          .eq('user_id', user.email)
          .maybeSingle();
        
        if (idError && !emailError) findError = idError;
        else if (idError) findError = emailError;
        else userRecord = idRecord;
      }

      if (findError) throw findError;

      if (!userRecord) {
        throw new Error('User not found in this Dealroom');
      }

      // Build update query using the identifier from the database
      const identifier = userRecord.user_id || userRecord.email;
      const identifierField = userRecord.user_id ? 'user_id' : 'email';

      const { error } = await supabase
        .from('dataroom_users')
        .update({
          status: 'inactive',
          last_active: new Date().toISOString()
        })
        .eq(identifierField, identifier)
        .eq('project_id', userProjectId);

      if (error) throw error;

      // Also revoke any pending invites
      await supabase
        .from('userinvites_invites')
        .update({ invite_status: 'revoked' })
        .eq('email', user.email)
        .eq('projectid', userProjectId)
        .eq('invite_status', 'pending');

      onMessage && onMessage(`Access revoked for ${user.email}`, 'success');
      loadWizardUsers(null);
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error revoking access:', error);
      onMessage && onMessage('Failed to revoke access: ' + error.message, 'error');
    }
  };

  // Delete user completely (hard delete)
  const handleDeleteUser = async (user) => {
    const userProjectId = user.parties?.[0]?.projectId || user.projectId || selectedProjectId;
    
    if (!userProjectId) {
      onMessage && onMessage('No project found for this user', 'error');
      return;
    }

    try {
      // First, find the user record to get the correct identifier
      // Try email first, then user_id if email doesn't match
      let userRecord = null;
      let findError = null;
      
      // Try to find by email first
      const { data: emailRecord, error: emailError } = await supabase
        .from('dataroom_users')
        .select('user_id, email')
        .eq('project_id', userProjectId)
        .eq('email', user.email)
        .maybeSingle();
      
      if (emailRecord) {
        userRecord = emailRecord;
      } else {
        // If not found by email, try by user_id (in case email is stored as user_id)
        const { data: idRecord, error: idError } = await supabase
          .from('dataroom_users')
          .select('user_id, email')
          .eq('project_id', userProjectId)
          .eq('user_id', user.email)
          .maybeSingle();
        
        if (idError && !emailError) findError = idError;
        else if (idError) findError = emailError;
        else userRecord = idRecord;
      }

      if (findError) throw findError;

      if (!userRecord) {
        throw new Error('User not found in this Dealroom');
      }

      // Build delete query using the identifier from the database
      const identifier = userRecord.user_id || userRecord.email;
      const identifierField = userRecord.user_id ? 'user_id' : 'email';

      const { error } = await supabase
        .from('dataroom_users')
        .delete()
        .eq(identifierField, identifier)
        .eq('project_id', userProjectId);

      if (error) throw error;

      // Also remove from party and team assignments
      // Use the same identifier for party/team tables
      await supabase
        .from('dataroom_user_parties')
        .delete()
        .eq('user_id', identifier)
        .eq('project_id', userProjectId);

      await supabase
        .from('dataroom_user_teams')
        .delete()
        .eq('user_id', identifier)
        .eq('project_id', userProjectId);

      // Optionally delete pending invites
      await supabase
        .from('userinvites_invites')
        .delete()
        .eq('email', user.email)
        .eq('projectid', userProjectId)
        .eq('invite_status', 'pending');

      onMessage && onMessage(`User ${user.email} deleted from DealRoom`, 'success');
      loadWizardUsers(null);
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      onMessage && onMessage('Failed to delete user: ' + error.message, 'error');
    }
  };

  // Rename user
  const handleRenameUser = async (updatedUser) => {
    const userProjectId = updatedUser.parties?.[0]?.projectId || selectedProjectId;
    
    if (!userProjectId) {
      onMessage && onMessage('No project found for this user', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('dataroom_users')
        .update({
          metadata: {
            ...(updatedUser.metadata || {}),
            first_name: updatedUser.firstName,
            last_name: updatedUser.lastName
          }
        })
        .eq('email', updatedUser.email)
        .eq('project_id', userProjectId);

      if (error) throw error;

      const displayName = `${updatedUser.firstName} ${updatedUser.lastName}`.trim();
      onMessage && onMessage(`User renamed to ${displayName}`, 'success');
      loadWizardUsers(null);
    } catch (error) {
      console.error('Error renaming user:', error);
      onMessage && onMessage('Failed to rename user: ' + error.message, 'error');
      throw error;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800"><Mail className="h-3 w-3 mr-1" /> Pending</span>;
      case 'invited':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><Mail className="h-3 w-3 mr-1" /> Invited</span>;
      case 'active':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><UserCheck className="h-3 w-3 mr-1" /> Active</span>;
      case 'blocked':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800"><Ban className="h-3 w-3 mr-1" /> Blocked</span>;
      case 'inactive':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><Ban className="h-3 w-3 mr-1" /> Revoked</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Unknown</span>;
    }
  };

  const canManage = userRole === 'creator' || userRole === 'support';

  // Group users by project
  const usersByProject = {};
  users.forEach(user => {
    const projectId = user.projectId;
    if (projectId) {
      if (!usersByProject[projectId]) {
        usersByProject[projectId] = [];
      }
      usersByProject[projectId].push(user);
    }
  });

  const totalUsers = users.length;
  const pendingUsers = users.filter(u => u.status === 'pending').length;
  const invitedUsers = users.filter(u => u.status === 'invited').length;

  console.log('UsersPage render - isLoadingUsers:', isLoadingUsers, 'usersError:', usersError, 'users count:', totalUsers);
  console.log('UsersPage - users data:', users);
  console.log('UsersPage - selectedProjectId:', selectedProjectId);

  if (isLoadingUsers) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <span className="text-gray-600">Loading users...</span>
        </div>
      </div>
    );
  }

  if (usersError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center">
          <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
          <div>
            <h3 className="text-sm font-medium text-red-800">Error Loading Users</h3>
            <p className="text-sm text-red-700 mt-1">{usersError}</p>
          </div>
        </div>
        <button
          onClick={() => loadWizardUsers(null)}
          className="mt-4 flex items-center px-3 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">DealRoom Users</h1>
          <p className="text-gray-600 mt-1">Manage users, their invite status, and assignments to parties and teams.</p>
          {selectedUsers.size > 0 && (
            <p className="text-sm text-blue-600 mt-2">
              {selectedUsers.size} user{selectedUsers.size > 1 ? 's' : ''} selected
            </p>
          )}
        </div>
        <div className="flex space-x-3">
          {canManage && selectedUsers.size > 0 && (
            <button
              onClick={sendInvitesToSelected}
              disabled={sendingInvites}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="h-4 w-4 mr-2" />
              {sendingInvites ? 'Sending...' : `Send Invites (${selectedUsers.size})`}
            </button>
          )}
          <button
            onClick={() => loadWizardUsers(null)}
            disabled={sendingInvites}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          {canManage && (
            <button
              onClick={() => setShowAddUserModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </button>
          )}
        </div>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">Total Users</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">{totalUsers}</dd>
            </dl>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">Pending Invites</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">{pendingUsers}</dd>
            </dl>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">Invited Users</dt>
              <dd className="mt-1 text-3xl font-semibold text-gray-900">{invitedUsers}</dd>
            </dl>
          </div>
        </div>
      </div>

      {/* Users Grouped by DealRoom */}
      {users.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No Users Yet</h3>
          <p className="text-gray-500 mb-6">
            Users are added through the Setup Wizard when you create parties and teams for a DealRoom project.
          </p>
          {canManage && (
            <button
              onClick={() => setShowAddUserModal(true)}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Your First User
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(usersByProject).map(([projectId, projectUsers]) => {
            const project = projects.find(p => p.id === parseInt(projectId));
            const projectName = project?.project_name || projectNames[parseInt(projectId)] || 'Unknown Dealroom';
            const isProjectExpanded = expandedProjects[projectId] !== false; // Default expanded
            
            return (
              <div key={projectId} className="bg-white rounded-lg shadow overflow-hidden border-2 border-gray-200">
                {/* DealRoom Header */}
                <div
                  className="bg-gradient-to-r from-blue-50 to-blue-100 border-b-2 border-blue-200 cursor-pointer"
                  onClick={() => toggleProjectExpansion(projectId)}
                >
                  <div className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center">
                      {isProjectExpanded ? (
                        <ChevronDown className="w-6 h-6 text-blue-600 mr-3" />
                      ) : (
                        <ChevronRight className="w-6 h-6 text-blue-600 mr-3" />
                      )}
                      <Building2 className="w-6 h-6 text-blue-600 mr-3" />
                      <div>
                        <h2 className="text-lg font-bold text-blue-900">
                          Dealroom: {projectName}
                        </h2>
                        <p className="text-sm text-blue-700">
                          {projectUsers.length} user{projectUsers.length === 1 ? '' : 's'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Users in this DealRoom */}
                {isProjectExpanded && (
                  <div className="divide-y divide-gray-200">
                    {projectUsers.map((userItem) => (
                      <div key={userItem.id} className={`px-6 py-4 hover:bg-gray-50 transition-colors ${selectedUsers.has(userItem.id) ? 'bg-blue-50' : ''}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1">
                            {canManage && (
                              <input
                                type="checkbox"
                                checked={selectedUsers.has(userItem.id)}
                                onChange={() => toggleUserSelection(userItem.id)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-4"
                              />
                            )}
                            <div className="flex-shrink-0 h-10 w-10 mr-4">
                              {userItem.avatarUrl ? (
                                <img className="h-10 w-10 rounded-full" src={userItem.avatarUrl} alt="" />
                              ) : (
                                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-gray-400">
                                  <User className="h-6 w-6 text-white" />
                                </span>
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <div className="text-sm font-medium text-gray-900">{userItem.fullName}</div>
                                {canManage && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRenameUser(userItem);
                                    }}
                                    className="text-gray-400 hover:text-blue-600 transition-colors"
                                    title="Rename user"
                                  >
                                    <Edit2 className="h-3 w-3" />
                                  </button>
                                )}
                              </div>
                              <div className="text-sm text-gray-500">{userItem.email}</div>
                              <div className="flex flex-wrap gap-2 mt-1">
                                {userItem.parties && userItem.parties.length > 0 ? (
                                  userItem.parties.map(party => (
                                    <span
                                      key={party.id}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                      style={{ backgroundColor: `${party.color}20`, color: party.color }}
                                      title={party.name}
                                    >
                                      <Building2 className="h-3 w-3 mr-1" /> Party: {party.name}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-xs text-gray-400 italic">No party assigned</span>
                                )}
                                {userItem.teams && userItem.teams.length > 0 && userItem.teams.map(team => (
                                  <span
                                    key={team.id}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800"
                                    title={team.name}
                                  >
                                    <UserCheck className="h-3 w-3 mr-1" /> Team: {team.name}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              {getStatusBadge(userItem.status)}
                              {userItem.invitedAt && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {userItem.status === 'invited' ? 'Sent' : 'Added'}: {formatDate(userItem.invitedAt)}
                                </div>
                              )}
                            </div>
                            {canManage && (
                              <div className="flex items-center space-x-2">
                                {userItem.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => sendSingleInvite(userItem)}
                                      disabled={sendingInvites}
                                      className="text-blue-600 hover:text-blue-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Send invite"
                                    >
                                      <Mail className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => setConfirmDelete({ user: userItem, action: 'delete' })}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete user"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                                {userItem.status === 'invited' && (
                                  <>
                                    <button
                                      onClick={() => sendSingleInvite(userItem)}
                                      disabled={sendingInvites}
                                      className="text-orange-600 hover:text-orange-900 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Resend invite"
                                    >
                                      <RefreshCw className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => setConfirmDelete({ user: userItem, action: 'revoke' })}
                                      className="text-orange-600 hover:text-orange-900"
                                      title="Revoke access"
                                    >
                                      <Ban className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => setConfirmDelete({ user: userItem, action: 'delete' })}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete user"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                                {userItem.status === 'active' && (
                                  <>
                                    <button
                                      onClick={() => setConfirmDelete({ user: userItem, action: 'block' })}
                                      className="text-red-600 hover:text-red-900"
                                      title="Block user (disable access)"
                                    >
                                      <Ban className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => setConfirmDelete({ user: userItem, action: 'delete' })}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete user"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                                {userItem.status === 'blocked' && (
                                  <>
                                    <button
                                      onClick={() => handleUnblockUser(userItem)}
                                      className="text-green-600 hover:text-green-900"
                                      title="Unblock user (restore access)"
                                    >
                                      <UserCheck className="h-4 w-4" />
                                    </button>
                                    <button
                                      onClick={() => setConfirmDelete({ user: userItem, action: 'delete' })}
                                      className="text-red-600 hover:text-red-900"
                                      title="Delete user"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </button>
                                  </>
                                )}
                                {userItem.status === 'inactive' && (
                                  <button
                                    onClick={() => setConfirmDelete({ user: userItem, action: 'delete' })}
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete user permanently"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add User Modal */}
      <AddUserModal
        isOpen={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        onComplete={(addedUsers) => {
          onMessage && onMessage(`Successfully added ${addedUsers.length} user(s)`, 'success');
          loadWizardUsers(null);
        }}
      />

      {/* Rename User Modal */}
      <RenameModal
        isOpen={!!renameUser}
        onClose={() => setRenameUser(null)}
        item={renameUser}
        itemType="user"
        onRename={handleRenameUser}
      />

      {/* Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                {confirmDelete.action === 'revoke' ? (
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center mr-4">
                    <Ban className="w-6 h-6 text-orange-600" />
                  </div>
                ) : confirmDelete.action === 'block' ? (
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                    <Ban className="w-6 h-6 text-red-600" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {confirmDelete.action === 'revoke' ? 'Revoke Access?' : confirmDelete.action === 'block' ? 'Block User?' : 'Delete User?'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {confirmDelete.user.email}
                  </p>
                </div>
              </div>

              {confirmDelete.action === 'revoke' ? (
                <div className="mb-6 space-y-2">
                  <p className="text-sm text-gray-700">
                    This will <strong>revoke access</strong> for this user:
                  </p>
                  <ul className="text-sm text-gray-600 list-disc list-inside space-y-1 ml-2">
                    <li>User status → Inactive</li>
                    <li>User cannot access DealRoom</li>
                    <li>Pending invites will be cancelled</li>
                    <li>User record is kept (can restore later)</li>
                  </ul>
                </div>
              ) : confirmDelete.action === 'block' ? (
                <div className="mb-6 space-y-2">
                  <p className="text-sm text-gray-700">
                    This will <strong>block</strong> this user from accessing this Dealroom:
                  </p>
                  <ul className="text-sm text-gray-600 list-disc list-inside space-y-1 ml-2">
                    <li>User status → Blocked</li>
                    <li>Viewer access disabled for this Dealroom</li>
                    <li>User record is kept (can unblock later)</li>
                    <li>Party/Team assignments remain intact</li>
                  </ul>
                </div>
              ) : (
                <div className="mb-6 space-y-2">
                  <p className="text-sm text-gray-700">
                    This will <strong>permanently delete</strong> this user:
                  </p>
                  <ul className="text-sm text-gray-600 list-disc list-inside space-y-1 ml-2">
                    <li>User removed from DealRoom</li>
                    <li>All assignments deleted</li>
                    <li>Pending invites deleted</li>
                    <li><strong className="text-red-600">This cannot be undone</strong></li>
                  </ul>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmDelete(null)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (confirmDelete.action === 'revoke') {
                      handleRevokeAccess(confirmDelete.user);
                    } else if (confirmDelete.action === 'block') {
                      handleBlockUser(confirmDelete.user);
                    } else {
                      handleDeleteUser(confirmDelete.user);
                    }
                  }}
                  className={`px-4 py-2 text-white rounded-lg ${
                    confirmDelete.action === 'revoke'
                      ? 'bg-orange-600 hover:bg-orange-700'
                      : confirmDelete.action === 'block'
                      ? 'bg-red-600 hover:bg-red-700'
                      : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {confirmDelete.action === 'revoke' ? 'Revoke Access' : confirmDelete.action === 'block' ? 'Block User' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(UsersPage);